import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListSubheader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Snackbar,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  Scoreboard as ScoreboardIcon,
  Timer as TimerIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  Replay as ReplayIcon,
  CheckCircle as CheckCircleIcon,
  CardGiftcard as RewardIcon,
  Sync as SyncIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import api, { extractApiError } from '../api';
import { useAuth } from '../AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';

const POLL_INTERVAL_MS = 15000;
const FAST_POLL_INTERVAL_MS = 5000;

function formatPrize(prize) {
  return `${prize.amount} × ${prize.assetTitle || prize.targetId}`;
}

function formatEventDateUtc(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds} UTC`;
}

function getEventStatus(event) {
  const now = Date.now();
  const startMs = Date.parse(event.startDate);
  const endMs = Date.parse(event.endDate);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 'ended';
  if (now >= startMs && now <= endMs) return 'ongoing';
  if (now < startMs) return 'upcoming';
  return 'ended';
}

function formatEventDate(dateString) {
  return formatEventDateUtc(dateString);
}

function getStatusLabel(status) {
  switch (status) {
    case 'ongoing': return 'Live';
    case 'upcoming': return 'Upcoming';
    case 'ended': return 'Ended';
    default: return status;
  }
}

function getStatusChipColor(status) {
  switch (status) {
    case 'ongoing': return 'success';
    case 'upcoming': return 'info';
    case 'ended': return 'default';
    default: return 'default';
  }
}

function formatEnumLabel(value) {
  if (!value || value === 'None') return null;
  return String(value).replace(/([a-z])([A-Z])/g, '$1 $2');
}

function RewardProcessingBadge({ status, compact = false }) {
  if (!status || status.eligibleCount === 0) return null;

  const { processedCount, eligibleCount, isComplete, hasStarted } = status;
  const label = `${processedCount}/${eligibleCount}`;

  if (isComplete) {
    return (
      <Chip
        icon={<CheckCircleIcon sx={{ fontSize: compact ? 14 : 16 }} />}
        label={label}
        size="small"
        color="success"
        sx={{ flexShrink: 0, height: compact ? 22 : 24 }}
      />
    );
  }

  if (hasStarted) {
    return (
      <Chip
        label={label}
        size="small"
        color="warning"
        sx={{ flexShrink: 0, height: compact ? 22 : 24 }}
      />
    );
  }

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{ flexShrink: 0, height: compact ? 22 : 24, color: 'text.secondary' }}
    />
  );
}

function ProcessedCell({ entry }) {
  if (!entry.prizeEligible) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  if (entry.rewardProcessed) {
    return (
      <Tooltip title="Rewards processed">
        <CheckCircleIcon color="success" fontSize="small" />
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Eligible — not processed yet">
      <RemoveIcon color="warning" fontSize="small" />
    </Tooltip>
  );
}

function EventOption({ event, rewardStatus }) {
  const status = getEventStatus(event);
  const metaParts = [
    formatEnumLabel(event.eventClass),
    formatEnumLabel(event.eventType),
    event.gameMode && event.gameMode !== 'None' ? formatEnumLabel(event.gameMode) : null,
  ].filter(Boolean);
  const showRewardBadge = status === 'ended' && rewardStatus?.eligibleCount > 0;

  return (
    <Stack spacing={0.5} sx={{ width: '100%', py: 0.25 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
          {event.title}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {showRewardBadge && <RewardProcessingBadge status={rewardStatus} compact />}
          <Chip
            label={getStatusLabel(status)}
            size="small"
            color={getStatusChipColor(status)}
            sx={{ height: 22 }}
          />
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {formatEventDate(event.startDate)} → {formatEventDate(event.endDate)}
      </Typography>
      {metaParts.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {metaParts.join(' · ')}
        </Typography>
      )}
    </Stack>
  );
}

function EventDashboard() {
  const { user } = useAuth();
  const { isDevelopment, environment } = useEnvironment();
  const queryClient = useQueryClient();
  const [selectedEventId, setSelectedEventId] = useState('');
  const [sendingRewards, setSendingRewards] = useState({});
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [recalcModalOpen, setRecalcModalOpen] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);
  const [recalcError, setRecalcError] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [processingTriggered, setProcessingTriggered] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const { data: rewardStatusMap } = useQuery({
    queryKey: ['eventsRewardStatus', environment],
    queryFn: async () => {
      const res = await api.get('/events/reward-status');
      return res.data;
    },
    refetchInterval: (query) => {
      const selected = query.state.data?.[selectedEventId];
      const inProgress = processingTriggered || (selected?.hasStarted && !selected?.isComplete);
      return inProgress ? FAST_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
    },
  });

  const selectedRewardStatus = useMemo(() => {
    if (!selectedEventId) return null;
    return rewardStatusMap?.[selectedEventId] ?? null;
  }, [rewardStatusMap, selectedEventId]);

  const isPollingFastForQuery = processingTriggered
    || (selectedRewardStatus?.hasStarted && !selectedRewardStatus?.isComplete);

  // Fetch all events
  const { data: events, isLoading: loadingEvents, error: errorEvents } = useQuery({
    queryKey: ['events', environment],
    queryFn: async () => {
      const res = await api.get('/events');
      return res.data;
    }
  });

  // Fetch leaderboard for selected event
  const {
    data: leaderboardData,
    isLoading: loadingLeaderboard,
    error: errorLeaderboard,
  } = useQuery({
    queryKey: ['eventLeaderboard', environment, selectedEventId],
    queryFn: async () => {
      if (!selectedEventId) return null;
      const res = await api.get(`/events/${selectedEventId}/leaderboard`);
      return res.data;
    },
    enabled: !!selectedEventId,
    refetchInterval: isPollingFastForQuery ? FAST_POLL_INTERVAL_MS : POLL_INTERVAL_MS,
  });

  const activeRewardStatus = leaderboardData?.rewardStatus ?? selectedRewardStatus;

  const isPollingFast = processingTriggered
    || (activeRewardStatus?.hasStarted && !activeRewardStatus?.isComplete);

  useEffect(() => {
    if (activeRewardStatus?.isComplete && processingTriggered) {
      setProcessingTriggered(false);
      setSnackbar({
        open: true,
        message: `All eligible rewards processed (${activeRewardStatus.processedCount}/${activeRewardStatus.eligibleCount}).`,
        severity: 'success',
      });
    }
  }, [activeRewardStatus?.isComplete, activeRewardStatus?.processedCount, activeRewardStatus?.eligibleCount, processingTriggered]);

  const isEventOngoing = (event) => event && getEventStatus(event) === 'ongoing';

  const groupedEvents = useMemo(() => {
    if (!events?.length) {
      return { ongoing: [], upcoming: [], ended: [] };
    }
    const enabled = events.filter((event) => event.enabled !== false);
    return {
      ongoing: enabled
        .filter((event) => getEventStatus(event) === 'ongoing')
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate)),
      upcoming: enabled
        .filter((event) => getEventStatus(event) === 'upcoming')
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate)),
      ended: enabled
        .filter((event) => getEventStatus(event) === 'ended')
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate)),
    };
  }, [events]);

  const selectedEvent = useMemo(
    () => events?.find((event) => event.id === selectedEventId) || null,
    [events, selectedEventId]
  );

  const renderEventMenuItems = () => {
    const groups = [
      { key: 'ongoing', label: 'Live now' },
      { key: 'upcoming', label: 'Upcoming' },
      { key: 'ended', label: 'Ended' },
    ];

    return groups.flatMap(({ key, label }) => {
      const groupEvents = groupedEvents[key];
      if (!groupEvents.length) return [];

      return [
        <ListSubheader key={`${key}-header`} sx={{ bgcolor: 'background.paper', lineHeight: 2.5 }}>
          {label}
        </ListSubheader>,
        ...groupEvents.map((event) => (
          <MenuItem key={event.id} value={event.id} sx={{ alignItems: 'flex-start', py: 1.25, whiteSpace: 'normal' }}>
            <EventOption event={event} rewardStatus={rewardStatusMap?.[event.id]} />
          </MenuItem>
        )),
      ];
    });
  };

  const handleProcessRewards = () => {
    setConfirmModalOpen(true);
  };

  const confirmProcessRewards = async () => {
    setConfirmModalOpen(false);
    setProcessLoading(true);
    setProcessingTriggered(true);

    try {
      await api.get(`/events/${selectedEventId}/process`);
      setSnackbar({
        open: true,
        message: 'Reward processing finished.',
        severity: 'success',
      });
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
      setSnackbar({
        open: true,
        message: isTimeout
          ? 'Request timed out — processing may still be running on the server. Status refreshes automatically.'
          : extractApiError(error, 'Failed to process rewards'),
        severity: isTimeout ? 'info' : 'error',
      });
    } finally {
      setProcessLoading(false);
      queryClient.invalidateQueries({ queryKey: ['eventsRewardStatus', environment] });
      queryClient.invalidateQueries({ queryKey: ['eventLeaderboard', environment, selectedEventId] });
    }
  };

  const getProcessButtonLabel = () => {
    if (processLoading) return 'Processing…';
    if (activeRewardStatus?.eligibleCount > 0) {
      const { processedCount, eligibleCount, isComplete } = activeRewardStatus;
      if (isComplete) return `Re-process Rewards (${processedCount}/${eligibleCount})`;
      if (processedCount > 0) return `Process Rewards (${processedCount}/${eligibleCount})`;
    }
    return 'Process Rewards';
  };

  const handleModalClose = () => {
    setConfirmModalOpen(false);
  };

  const calculateEventStats = (leaderboard) => {
    if (!leaderboard) return null;
    
    const totalParticipants = leaderboard.length;
    const totalScore = leaderboard.reduce((sum, entry) => sum + entry.score, 0);
    const averageScore = totalParticipants > 0 ? Math.round(totalScore / totalParticipants) : 0;
    const topScore = leaderboard[0]?.score || 0;
    
    return {
      totalParticipants,
      averageScore,
      topScore
    };
  };

  const formatDate = (dateString) => formatEventDateUtc(dateString);

  const handleSendRewards = async (userId) => {
    if (!userId || !selectedEventId) return;

    setSendingRewards(prev => ({ ...prev, [userId]: true }));

    try {
      await api.post(`/events/${selectedEventId}/send-nft-rewards`, {
        userId
      });
      alert('NFT rewards sent successfully');
    } catch (error) {
      alert('Failed to send NFT rewards: ' + error.message);
    } finally {
      setSendingRewards(prev => ({ ...prev, [userId]: false }));
    }
  };

  const hasNFTRewards = (prizes) => {
    return prizes.some(prize => {
      try {
        const config = JSON.parse(prize.config || '{}');
        return config.isNFT;
      } catch {
        return false;
      }
    });
  };

  const generateCSV = (leaderboard) => {
    if (!leaderboard || leaderboard.length === 0) return '';
    
    // Get top 50 entries
    const top50 = leaderboard.slice(0, 50);
    
    // CSV headers
    const headers = ['Rank', 'Name', 'Email', 'Wallet', 'Created Date', 'Score'];
    
    // CSV rows
    const rows = top50.map((entry, idx) => [
      idx + 1, // Rank
      entry.name || 'Unknown',
      entry.email || 'N/A',
      entry.wallet || 'N/A',
      entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : 'N/A',
      entry.score
    ]);
    
    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    return csvContent;
  };

  const handleExportCSV = async () => {
    if (!leaderboardData?.leaderboard) {
      alert('No leaderboard data available');
      return;
    }

    try {
      const csvContent = generateCSV(leaderboardData.leaderboard);
      await navigator.clipboard.writeText(csvContent);
      alert('CSV data copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy CSV to clipboard:', error);
      alert('Failed to copy CSV to clipboard');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!leaderboardData?.leaderboard) return;
    if (selectedUserIds.size === leaderboardData.leaderboard.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(leaderboardData.leaderboard.map((e) => e.userId)));
    }
  };

  const runRecalculate = async (saveToDatabase) => {
    if (!selectedEventId) return;
    setRecalcLoading(true);
    setRecalcError(null);
    setRecalcResult(null);
    try {
      const params = new URLSearchParams();
      if (saveToDatabase) params.append('saveToDatabase', 'true');
      if (selectedUserIds.size > 0) {
        params.append('userIds', Array.from(selectedUserIds).join(','));
      }
      const url = `/events/${selectedEventId}/recalculate${params.toString() ? `?${params}` : ''}`;
      const res = await api.get(url);
      setRecalcResult(res.data);
      if (saveToDatabase) {
        queryClient.invalidateQueries({ queryKey: ['eventLeaderboard', environment, selectedEventId] });
      }
    } catch (error) {
      setRecalcError(error.response?.data?.error || error.message);
    } finally {
      setRecalcLoading(false);
    }
  };

  const closeRecalcModal = () => {
    setRecalcModalOpen(false);
    setRecalcResult(null);
    setRecalcError(null);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h4">Event Dashboard</Typography>
        {isPollingFast && (
          <Chip
            icon={<SyncIcon sx={{ animation: 'spin 1.2s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />}
            label={`Refreshing every ${FAST_POLL_INTERVAL_MS / 1000}s`}
            size="small"
            color="info"
            variant="outlined"
          />
        )}
      </Stack>
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Select Event</InputLabel>
          <Select
            value={selectedEventId}
            label="Select Event"
            onChange={e => setSelectedEventId(e.target.value)}
            renderValue={() => {
              if (!selectedEvent) return 'Select an event';
              const meta = [
                formatEnumLabel(selectedEvent.eventClass),
                formatEnumLabel(selectedEvent.eventType),
              ].filter(Boolean).join(' · ');
              return (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25 }}>
                  <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {selectedEvent.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {formatEventDate(selectedEvent.startDate)} → {formatEventDate(selectedEvent.endDate)}
                      {meta ? ` · ${meta}` : ''}
                    </Typography>
                  </Stack>
                  {getEventStatus(selectedEvent) === 'ended' && (
                    <RewardProcessingBadge status={activeRewardStatus} />
                  )}
                </Stack>
              );
            }}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 480, maxWidth: 720 },
              },
            }}
          >
            {loadingEvents ? (
              <MenuItem disabled>Loading events...</MenuItem>
            ) : errorEvents ? (
              <MenuItem disabled>Error loading events</MenuItem>
            ) : (
              renderEventMenuItems()
            )}
          </Select>
        </FormControl>
      </Paper>
      {loadingLeaderboard && <CircularProgress />}
      {errorLeaderboard && <Alert severity="error">Failed to load leaderboard</Alert>}
      {leaderboardData && (
        <Box>
          {isPollingFast && activeRewardStatus?.eligibleCount > 0 && !activeRewardStatus.isComplete && (
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Reward processing progress
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {activeRewardStatus.processedCount}/{activeRewardStatus.eligibleCount} processed
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  100,
                  (activeRewardStatus.processedCount / activeRewardStatus.eligibleCount) * 100
                )}
              />
            </Box>
          )}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Typography variant="h5">{leaderboardData.event.title}</Typography>
                {getEventStatus(leaderboardData.event) === 'ended' && activeRewardStatus?.isComplete && (
                  <Tooltip title="All eligible rewards processed">
                    <CheckCircleIcon color="success" />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="body1" gutterBottom dangerouslySetInnerHTML={{ __html: leaderboardData.event.description }} />
              {activeRewardStatus?.eligibleCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Rewards: {activeRewardStatus.processedCount}/{activeRewardStatus.eligibleCount} eligible players processed
                  {activeRewardStatus.lastProcessedAt && (
                    <> · last activity {formatDate(activeRewardStatus.lastProcessedAt)}</>
                  )}
                </Typography>
              )}
            </Box>
            {user?.role !== 'manager' && (
              <Button
                variant={activeRewardStatus?.isComplete ? 'outlined' : 'contained'}
                onClick={handleProcessRewards}
                disabled={isEventOngoing(leaderboardData.event) || processLoading}
              >
                {getProcessButtonLabel()}
              </Button>
            )}
          </Stack>

          {/* Event Statistics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <PeopleIcon color="primary" />
                    <Typography variant="h6">Participants</Typography>
                  </Stack>
                  <Typography variant="h4">
                    {calculateEventStats(leaderboardData.leaderboard)?.totalParticipants || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <ScoreboardIcon color="primary" />
                    <Typography variant="h6">Average Score</Typography>
                  </Stack>
                  <Typography variant="h4">
                    {calculateEventStats(leaderboardData.leaderboard)?.averageScore.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <TrophyIcon color="primary" />
                    <Typography variant="h6">Top Score</Typography>
                  </Stack>
                  <Typography variant="h4">
                    {calculateEventStats(leaderboardData.leaderboard)?.topScore.toLocaleString() || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <TimerIcon color="primary" />
                    <Typography variant="h6">Status</Typography>
                  </Stack>
                  <Typography variant="h4" color={isEventOngoing(leaderboardData.event) ? "success.main" : "text.secondary"}>
                    {isEventOngoing(leaderboardData.event) ? "Active" : "Ended"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(leaderboardData.event.startDate)} - {formatDate(leaderboardData.event.endDate)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 3
              }}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <RewardIcon color="primary" />
                    <Typography variant="h6">Rewards</Typography>
                  </Stack>
                  {activeRewardStatus?.eligibleCount > 0 ? (
                    <>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h4">
                          {activeRewardStatus.processedCount}/{activeRewardStatus.eligibleCount}
                        </Typography>
                        {activeRewardStatus.isComplete && <CheckCircleIcon color="success" />}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {activeRewardStatus.isComplete ? 'All processed' : 'Eligible processed'}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="h4" color="text.secondary">N/A</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action buttons */}
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2, mb: 1 }}>
            {user?.role !== 'manager' && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ReplayIcon />}
                onClick={() => setRecalcModalOpen(true)}
                disabled={!leaderboardData?.leaderboard}
              >
                Recalculate Scores
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              disabled={!leaderboardData?.leaderboard || leaderboardData.leaderboard.length === 0}
            >
              Export Top 50 as CSV
            </Button>
          </Stack>

          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table>
              <TableHead>
                <TableRow>
                  {user?.role !== 'manager' && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={leaderboardData?.leaderboard?.length > 0 && selectedUserIds.size === leaderboardData.leaderboard.length}
                        indeterminate={selectedUserIds.size > 0 && selectedUserIds.size < (leaderboardData?.leaderboard?.length || 0)}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell>Rank</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Wallet</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Prizes</TableCell>
                  <TableCell align="center">Processed</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboardData.leaderboard.map((entry, idx) => (
                  <TableRow key={entry.userId}>
                    {user?.role !== 'manager' && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.has(entry.userId)}
                          onChange={() => toggleUserSelection(entry.userId)}
                        />
                      </TableCell>
                    )}
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{entry.name || 'Unknown'}</TableCell>
                    <TableCell>{entry.email || 'N/A'}</TableCell>
                    <TableCell>{entry.wallet || 'N/A'}</TableCell>
                    <TableCell>{entry.createdAt ? formatDate(entry.createdAt) : 'N/A'}</TableCell>
                    <TableCell>{entry.score.toLocaleString()}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {entry.prizes?.map((prize, prizeIdx) => (
                          <Chip
                            key={prizeIdx}
                            label={formatPrize(prize)}
                            size="small"
                            color={prize.isNFT ? 'success' : 'default'}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <ProcessedCell entry={entry} />
                    </TableCell>
                    <TableCell>
                      {hasNFTRewards(entry.prizes) && (
                        <IconButton
                          size="small"
                          onClick={() => handleSendRewards(entry.userId)}
                          disabled={sendingRewards[entry.userId]}
                        >
                          <SendIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Confirmation Modal for Process Rewards */}
      <Dialog
        open={confirmModalOpen}
        onClose={handleModalClose}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Process Rewards
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {leaderboardData?.event && (
              <>
                <Typography variant="body1" gutterBottom>
                  Are you sure you want to process rewards for the event:
                </Typography>
                <Typography variant="h6" gutterBottom color="primary">
                  {leaderboardData.event.title}
                </Typography>
                
                {/* Event Status Check */}
                {isEventOngoing(leaderboardData.event) ? (
                  <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                    <strong>Warning:</strong> This event is still ongoing. 
                    Event ends: {formatDate(leaderboardData.event.endDate)}
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                    Event ended: {formatDate(leaderboardData.event.endDate)}
                    {activeRewardStatus?.eligibleCount > 0 && (
                      <>
                        <br />
                        Current progress: {activeRewardStatus.processedCount}/{activeRewardStatus.eligibleCount} eligible players processed.
                      </>
                    )}
                  </Alert>
                )}

                <Alert severity="info" sx={{ mb: 2 }}>
                  Processing runs on the game server and may take several minutes. If the request times out,
                  rewards may still be created — this page refreshes automatically every {FAST_POLL_INTERVAL_MS / 1000}s while in progress.
                </Alert>

                {/* API Endpoint Information */}
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>API Endpoint:</strong>
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    backgroundColor: 'background.default', 
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1, 
                    borderRadius: 1,
                    wordBreak: 'break-all'
                  }}
                >
                  GET {isDevelopment ? 'dev-api' : 'beta-api'}.paradisetycoon.com/events/process?eventId={'{selectedEventId}'} (via PTAdmin proxy)
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Event ID:</strong> {selectedEventId}
                </Typography>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={confirmProcessRewards} 
            color="primary" 
            variant="contained"
            disabled={(leaderboardData?.event && isEventOngoing(leaderboardData.event)) || processLoading}
          >
            {processLoading ? 'Starting…' : 'Confirm Process Rewards'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recalculate Scores Dialog */}
      <Dialog
        open={recalcModalOpen}
        onClose={closeRecalcModal}
        maxWidth="md"
        fullWidth
        aria-labelledby="recalc-dialog-title"
      >
        <DialogTitle id="recalc-dialog-title">Recalculate Event Scores</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              Recalculate scores from raw DataPoints. Use <strong>Preview</strong> to see recalculated scores and
              days-with-scores without changing the database. Use <strong>Recalculate &amp; Save</strong> to update
              the leaderboard in the database.
            </Typography>
            {selectedUserIds.size > 0 ? (
              <Typography variant="body2" color="text.secondary">
                Recalculating <strong>{selectedUserIds.size} selected users</strong> only.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Recalculating <strong>all participants</strong> with event data.
              </Typography>
            )}
          </DialogContentText>

          {recalcError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRecalcError(null)}>
              {recalcError}
            </Alert>
          )}

          {recalcLoading && (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <CircularProgress size={24} />
              <Typography>Recalculating scores…</Typography>
            </Stack>
          )}

          {recalcResult && !recalcResult.savedToDatabase && recalcResult.leaderboard && (() => {
            const existingByUserId = {};
            (leaderboardData?.leaderboard || []).forEach((e, idx) => {
              existingByUserId[e.userId] = { score: e.score, rank: idx + 1 };
            });
            const formatChange = (oldVal, newVal) => {
              if (oldVal == null || oldVal === undefined) return { text: '–', color: 'inherit' };
              const diff = (newVal ?? 0) - (oldVal ?? 0);
              if (diff === 0) return { text: 'same', color: 'text.secondary' };
              const sign = diff > 0 ? '+' : '';
              return { text: `${sign}${diff.toLocaleString()}`, color: diff > 0 ? 'success.main' : 'error.main' };
            };
            const formatRankChange = (oldRank, newRank) => {
              if (oldRank == null || oldRank === undefined) return '–';
              const diff = (newRank ?? 0) - (oldRank ?? 0);
              if (diff === 0) return '−';
              const sign = diff < 0 ? '' : '+';
              return `${sign}${diff}`;
            };
            return (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview – {recalcResult.totalEntries} users with scores (not saved)
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Rank</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell align="right">Old Score</TableCell>
                        <TableCell align="right">New Score</TableCell>
                        <TableCell align="right">Change</TableCell>
                        <TableCell align="center">Rank Δ</TableCell>
                        <TableCell align="right">Data Points</TableCell>
                        <TableCell align="right">Days w/ Scores</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recalcResult.leaderboard.map((entry) => {
                        const existing = existingByUserId[entry.userId];
                        const oldScore = existing?.score;
                        const scoreChange = formatChange(oldScore, entry.score);
                        const rankChange = formatRankChange(existing?.rank, entry.rank);
                        const hasRankChange = rankChange !== '−';
                        return (
                          <TableRow key={entry.userId}>
                            <TableCell>{entry.rank}</TableCell>
                            <TableCell>{entry.userName}</TableCell>
                            <TableCell align="right">{oldScore != null ? oldScore.toLocaleString() : '–'}</TableCell>
                            <TableCell align="right">{entry.score != null ? entry.score.toLocaleString() : '–'}</TableCell>
                            <TableCell align="right" sx={{ color: scoreChange.color, fontWeight: scoreChange.text !== 'same' ? 600 : 400 }}>
                              {scoreChange.text}
                            </TableCell>
                            <TableCell align="center" sx={{ color: hasRankChange ? (rankChange.startsWith('+') ? 'error.main' : 'success.main') : 'text.secondary' }}>
                              {rankChange}
                            </TableCell>
                            <TableCell align="right">{entry.dataPointCount ?? '–'}</TableCell>
                            <TableCell align="right">{entry.daysWithScores ?? '–'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })()}

          {recalcResult?.savedToDatabase && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {recalcResult.message || `Saved scores for ${recalcResult.processedUsers} users.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRecalcModal} color="inherit">
            Close
          </Button>
          <Button
            onClick={() => runRecalculate(false)}
            variant="outlined"
            disabled={recalcLoading}
          >
            Preview
          </Button>
          <Button
            onClick={() => runRecalculate(true)}
            variant="contained"
            color="primary"
            disabled={recalcLoading}
          >
            Recalculate &amp; Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EventDashboard; 